// Cues & notes: two views. Cues: one cue list per department, in show order, with duplicate and
// out-of-sequence flags. Notes: every note in show order, filterable by note layer.

import { useMemo, useState } from "preact/hooks";
import { cueRows, noteRows } from "../../format/derive.ts";
import type { Note } from "../../format/types.ts";
import { openEdit } from "../actions.ts";
import { cueListOpen, cueListView, ix, type MarkRef } from "../state.ts";
import { reveal } from "../viewer.ts";
import { chipColors } from "./kit.tsx";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** What a note does, when its text alone may not say: strikes cut, inside boxes replace. */
const noteKind = (n: Note) => (n.style === "strike" ? "Cut" : n.placement === "inside" ? "Rewrite" : null);

export function CueList({ readOnly }: { readOnly: boolean }) {
  const index = ix.value;
  const view = cueListView.value;
  const [deptPick, setDept] = useState("all");
  const [layerPick, setLayer] = useState("all");
  const cues = useMemo(() => (index ? cueRows(index) : []), [index]);
  const notes = useMemo(() => (index ? noteRows(index) : []), [index]);
  if (!cueListOpen.value || !index) return null;
  // A deleted department or layer falls back to All rather than an empty, tab-less list.
  const dept = index.departments.has(deptPick) ? deptPick : "all";
  const layer = index.layers.get(layerPick);
  const shownCues = dept === "all" ? cues : cues.filter((r) => r.cue.department === dept);
  const shownNotes = layer ? notes.filter((r) => r.note.layer === layer.id) : notes;
  const flagged = shownCues.filter((r) => r.flags.length).length;
  const count = view === "cues" ? [plural(shownCues.length, "cue"), flagged && `${flagged} flagged`].filter(Boolean).join(" · ") : plural(shownNotes.length, "note");
  const pageHead = index.hasPageLabels() ? "Page" : "PDF page";
  const open = (ref: MarkRef, page: number) => {
    if (readOnly) return;
    reveal(ref, page);
    openEdit(ref);
  };

  return (
    <aside class="drawer cue-list" aria-label="Cues & notes">
      <div class="drawer-head">
        <strong>Cues & notes</strong>
        <span class="count">{count}</span>
        <span class="spacer" />
        <button type="button" data-tooltip="Close cue list" data-tooltip-description="Close Cues & notes without saving or discarding an open draft." data-tooltip-keys="Esc" onClick={() => (cueListOpen.value = false)}>
          Close
        </button>
      </div>
      {readOnly && <p class="cue-list-loading hint" role="status">Cues and notes are read-only until the script is ready.</p>}
      <div class="list-views">
        <div class="segmented" role="group" aria-label="List">
          <button type="button" aria-pressed={view === "cues"} onClick={() => (cueListView.value = "cues")}>
            Cues
          </button>
          <button type="button" aria-pressed={view === "notes"} onClick={() => (cueListView.value = "notes")}>
            Notes
          </button>
        </div>
      </div>
      <div class="tabs">
        {view === "cues" ? (
          <>
            <button type="button" aria-pressed={dept === "all"} onClick={() => setDept("all")}>
              All
            </button>
            {index.sortedDepartments.map((d) => (
              <button type="button" style={chipColors(d.color)} class="dept-tab" aria-pressed={dept === d.id} onClick={() => setDept(d.id)}>
                {d.code}
              </button>
            ))}
          </>
        ) : (
          <>
            <button type="button" aria-pressed={!layer} onClick={() => setLayer("all")}>
              All
            </button>
            {index.sortedLayers.map((l) => (
              <button type="button" style={chipColors(l.color)} class="dept-tab" aria-pressed={layer?.id === l.id} onClick={() => setLayer(l.id)}>
                {l.name}
              </button>
            ))}
          </>
        )}
      </div>
      <div class="table-wrap">
        {view === "cues" ? (
          <>
            <table>
              <thead>
                <tr>
                  <th>Dept</th>
                  <th>Cue</th>
                  <th>Label</th>
                  <th>{pageHead}</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {shownCues.map((r, i) => {
                  const multi = index.cuesOf(r.moment.id).length > 1;
                  const groupStart = multi && shownCues[i - 1]?.moment.id !== r.moment.id;
                  return (
                    <tr class={`${multi ? "multi" : ""} ${groupStart ? "group-start" : ""}`} aria-disabled={readOnly} onClick={() => open({ kind: "moment", id: r.moment.id }, r.page)}>
                      <td class="dept" style={chipColors(r.department?.color ?? "#888888")}>
                        {r.code}
                      </td>
                      <td class="num">{r.cue.number}</td>
                      <td>
                        {r.cue.label}
                        {r.moment.scriptText && <div class="script">“{r.moment.scriptText}”</div>}
                      </td>
                      <td class="page">{r.pageLabel}</td>
                      <td class="flags">{r.flags.map((f) => <div class="flag">⚠ {f}</div>)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!shownCues.length && <p class="empty">No cues yet. Mark one with the Pencil (or a mouse): drag over a line, or tap for a pin.</p>}
          </>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Layer</th>
                  <th>Note</th>
                  <th>{pageHead}</th>
                </tr>
              </thead>
              <tbody>
                {shownNotes.map((r) => {
                  const kind = noteKind(r.note);
                  return (
                    <tr class="note" aria-disabled={readOnly} onClick={() => open({ kind: "note", id: r.note.id }, r.page)}>
                      <td class="dept layer" style={chipColors(r.layer?.color ?? "#888888")}>
                        {r.layer?.name ?? "?"}
                      </td>
                      <td>
                        {kind && <span class="note-kind">{kind}</span>}
                        {r.note.text ? <span class="note-text">{r.note.text}</span> : !kind && <span class="note-text empty-text">No text</span>}
                      </td>
                      <td class="page">{r.pageLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!shownNotes.length && <p class="empty">{layer ? `No ${layer.name} notes yet.` : "No notes yet. Add one from the editor's Note tab."}</p>}
          </>
        )}
      </div>
    </aside>
  );
}
